import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional custom fallback. Receives the caught error. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches unhandled render errors in the React tree and shows a recovery UI
 * instead of a blank white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[InvestAlbum] Unhandled render error:', error, info.componentStack)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    const { error } = this.state
    const { children, fallback } = this.props

    if (error) {
      if (fallback) {
        return fallback(error, this.reset)
      }

      return (
        <div className="error-boundary-shell" role="alert" aria-live="assertive">
          <div className="error-boundary-card">
            <p className="eyebrow" style={{ color: '#dc2626' }}>
              Unexpected error
            </p>
            <h2>Something went wrong</h2>
            <p className="error-boundary-message">{error.message}</p>
            <div className="error-boundary-actions">
              <button className="primary-button" onClick={this.reset} type="button">
                Try again
              </button>
              <button
                className="ghost-button"
                onClick={() => window.location.reload()}
                type="button"
              >
                Reload app
              </button>
            </div>
            <details className="error-boundary-details">
              <summary>Technical details</summary>
              <pre>{error.stack}</pre>
            </details>
          </div>
        </div>
      )
    }

    return children
  }
}
